import React from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import {
  Drawer,
  IconButton,
  Box,
  Typography,
} from "@mui/material";

import {
  ChevronLeft,
  Menu as MenuIcon,
} from "@mui/icons-material";

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 72;

export default function DrawerMain(props) {
  const { open, onToggleDrawer, viewport, menuList, isDarkMode } = props;
  // Keep this threshold in sync with Main.js — below 960 the drawer
  // becomes a modal-style overlay (temporary variant); above, it's
  // a persistent side-shelf that shares layout width with the page.
  const isSmallScreen = viewport < 960;

  // Theme-responsive colors
  const drawerBg = isDarkMode
    ? "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)"
    : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)";
  const textColor = isDarkMode ? "#fff" : "#334155";
  const borderColor = isDarkMode ? "rgba(255,255,255,0.08)" : "#e2e8f0";

  return (
    <Drawer
      variant={isSmallScreen ? "temporary" : "persistent"}
      open={open}
      anchor="left"
      onClose={isSmallScreen ? onToggleDrawer : undefined}
      ModalProps={{ ...(isSmallScreen ? { keepMounted: true } : {}) }}
      sx={{
        height: "100%",
        ...(!isSmallScreen
          ? {
              width: open ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
              flexShrink: 0,
              position: "fixed",
              "& .MuiDrawer-paper": {
                width: open ? DRAWER_WIDTH_EXPANDED : DRAWER_WIDTH_COLLAPSED,
                transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease",
                boxShadow: isDarkMode ? "4px 0 30px rgba(0,0,0,0.25)" : "4px 0 20px rgba(0,0,0,0.08)",
                visibility: "visible !important",
                transform: "none !important",
                background: drawerBg,
                color: textColor,
                overflowX: "hidden",
                borderRight: `1px solid ${borderColor}`,
                borderRadius: "0 16px 16px 0",
                margin: "8px 0 8px 0",
                height: "calc(100% - 16px)",
              },
            }
          : {
              width: open ? DRAWER_WIDTH_EXPANDED : 0,
              "& .MuiDrawer-paper": {
                background: drawerBg,
                color: textColor,
                width: DRAWER_WIDTH_EXPANDED,
                transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), background 0.3s ease",
              },
            }),
      }}
    >
      {/* Header */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "space-between" : "center",
          padding: open ? "0.75rem 1rem" : "0.75rem",
          height: "64px",
          minHeight: "64px",
          background: "linear-gradient(135deg, #F97316 0%, #C2410C 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.15)",
        }}
      >
        <AnimatePresence mode="wait">
          {open && (
            <motion.div
              key="logo"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <Link to="/" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    src={process.env.PUBLIC_URL + "/logo96.png"}
                    alt="logo"
                    style={{ height: "36px", filter: "brightness(0) invert(1)" }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      fontWeight: 700,
                      letterSpacing: "-0.5px",
                      fontSize: "1.1rem",
                    }}
                  >
                    Clue2App
                  </Typography>
                </Box>
              </Link>
            </motion.div>
          )}
        </AnimatePresence>

        <IconButton
          component={motion.button}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onToggleDrawer}
          size="small"
          sx={{
            color: "#fff",
            background: "rgba(255,255,255,0.15)",
            width: 36,
            height: 36,
            "&:hover": { background: "rgba(255,255,255,0.25)" },
          }}
        >
          {open ? <ChevronLeft /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          overflow: "auto",
          py: 1,
          flex: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: isDarkMode ? "rgba(234, 88, 12, 0.4)" : "rgba(234, 88, 12, 0.3)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
          },
        }}
        component="nav"
      >
        {menuList}
      </Box>

      {/* Footer */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        sx={{
          padding: open ? "1rem 1.5rem" : "1rem",
          borderTop: `1px solid ${borderColor}`,
          background: isDarkMode ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.03)",
        }}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#94a3b8" : "#64748b", display: "block", fontWeight: 500 }}
              >
                Clue2App Platform
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#64748b" : "#94a3b8", fontSize: "0.7rem" }}
              >
                v2.0.0
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Drawer>
  );
}
